import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarIcon,
  ChevronDownIcon,
  DownloadIcon,
  MailIcon,
  MapPinIcon,
  SearchIcon,
  Settings2Icon,
  TicketIcon,
  TrophyIcon,
  XCircleIcon,
  XIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { listLeads, listUserLeads, authClient, type LeadRecord } from '@/lib/auth-client';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { SortHeaderButton, ToolbarButton, TruncatedValue } from '@/components/ui/table-shared';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { UsersTableColumnsPopover, type UsersTableColumnOption } from '@/components/users-table-columns-popover';

type ResultFilter = 'all' | 'lost' | 'won';
type LeadSortPreset =
  | 'createdAt:desc'
  | 'createdAt:asc'
  | 'name:asc'
  | 'name:desc'
  | 'provenance:asc'
  | 'provenance:desc'
  | 'lot:asc'
  | 'lot:desc'
  | 'result:desc'
  | 'result:asc';

const pageSizeOptions = [20, 50, 100] as const;
const LEADS_TABLE_COLUMNS_STORAGE_KEY = 'vibecode-admin-leads-table-columns';

const resultFilterOptions: Array<{ label: string; value: ResultFilter }> = [
  { label: 'Tous les résultats', value: 'all' },
  { label: 'Gagné', value: 'won' },
  { label: 'Perdu', value: 'lost' },
];

const sortPresetOptions: Array<{ label: string; value: LeadSortPreset }> = [
  { label: 'Ordre personnalisé', value: 'createdAt:desc' },
  { label: 'Plus récents', value: 'createdAt:desc' },
  { label: 'Plus anciens', value: 'createdAt:asc' },
  { label: 'Nom (A-Z)', value: 'name:asc' },
  { label: 'Nom (Z-A)', value: 'name:desc' },
  { label: 'Provenance (A-Z)', value: 'provenance:asc' },
  { label: 'Provenance (Z-A)', value: 'provenance:desc' },
  { label: 'Lot (A-Z)', value: 'lot:asc' },
  { label: 'Lot (Z-A)', value: 'lot:desc' },
  { label: 'Gagnants d’abord', value: 'result:desc' },
];

type LeadTableColumnKey = 'contact' | 'provenance' | 'result' | 'lot' | 'date';

const defaultTableColumns: Array<UsersTableColumnOption & { key: LeadTableColumnKey }> = [
  { key: 'contact', label: 'Contact', visible: true },
  { key: 'provenance', label: 'Provenance', visible: true },
  { key: 'result', label: 'Résultat', visible: true },
  { key: 'lot', label: 'Lot', visible: true },
  { key: 'date', label: 'Date', visible: true },
];

function readStoredTableColumns() {
  if (typeof window === 'undefined') {
    return defaultTableColumns;
  }

  try {
    const raw = window.localStorage.getItem(LEADS_TABLE_COLUMNS_STORAGE_KEY);
    if (!raw) {
      return defaultTableColumns;
    }

    const parsed = JSON.parse(raw) as Array<Partial<UsersTableColumnOption> & { key?: string }>;
    const defaults = new Map(defaultTableColumns.map((column) => [column.key, column]));
    const normalized = parsed
      .map((column) => {
        if (!column.key) return null;
        const defaultColumn = defaults.get(column.key as LeadTableColumnKey);
        if (!defaultColumn) return null;
        return {
          ...defaultColumn,
          visible: typeof column.visible === 'boolean' ? column.visible : defaultColumn.visible,
        };
      })
      .filter((column): column is (typeof defaultTableColumns)[number] => column !== null);

    const missing = defaultTableColumns.filter((column) => !normalized.some((item) => item.key === column.key));
    const merged = [...normalized, ...missing];

    if (merged.every((column) => !column.visible)) {
      return defaultTableColumns;
    }

    return merged;
  } catch {
    return defaultTableColumns;
  }
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function getPaginationItems(page: number, pageCount: number) {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, pageCount, page, page - 1, page + 1]);

  if (page <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }

  if (page >= pageCount - 2) {
    pages.add(pageCount - 1);
    pages.add(pageCount - 2);
    pages.add(pageCount - 3);
  }

  return Array.from(pages).filter((value) => value >= 1 && value <= pageCount).sort((left, right) => left - right);
}

function parseSortPreset(preset: LeadSortPreset) {
  const [sortKey, sortDirection] = preset.split(':') as [string, 'asc' | 'desc'];
  return { sortDirection, sortKey };
}

function getSortPresetLabel(preset: LeadSortPreset) {
  return sortPresetOptions.find((option) => option.value === preset)?.label ?? 'Ordre d’affichage';
}

function getSortState(preset: LeadSortPreset, columnKey: LeadTableColumnKey) {
  const { sortKey, sortDirection } = parseSortPreset(preset);

  if (columnKey === 'contact' && sortKey === 'name') {
    return { active: true, direction: sortDirection };
  }

  if (columnKey === 'result' && sortKey === 'result') {
    return { active: true, direction: sortDirection };
  }

  if (columnKey === 'provenance' && sortKey === 'provenance') {
    return { active: true, direction: sortDirection };
  }

  if (columnKey === 'lot' && sortKey === 'lot') {
    return { active: true, direction: sortDirection };
  }

  if (columnKey === 'date' && sortKey === 'createdAt') {
    return { active: true, direction: sortDirection };
  }

  return { active: false, direction: 'desc' as const };
}

function escapeExcelCell(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function excelColumnName(columnIndex: number) {
  let current = columnIndex + 1;
  let label = '';

  while (current > 0) {
    const remainder = (current - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    current = Math.floor((current - 1) / 26);
  }

  return label;
}

function createWorksheetXml(headers: string[], rows: string[][]) {
  const allRows = [headers, ...rows];
  const worksheetRows = allRows
    .map((row, rowIndex) => {
      const cells = row
        .map((cell, columnIndex) => {
          const reference = `${excelColumnName(columnIndex)}${rowIndex + 1}`;
          const styleAttribute = rowIndex === 0 ? ' s="1"' : '';
          return `<c r="${reference}" t="inlineStr"${styleAttribute}><is><t>${escapeExcelCell(cell)}</t></is></c>`;
        })
        .join('');

      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetData>${worksheetRows}</sheetData>
</worksheet>`;
}

function createWorkbookXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook
  xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
>
  <sheets>
    <sheet name="Leads" sheetId="1" r:id="rId1" />
  </sheets>
</workbook>`;
}

function createWorkbookRelsXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship
    Id="rId1"
    Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet"
    Target="worksheets/sheet1.xml"
  />
  <Relationship
    Id="rId2"
    Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles"
    Target="styles.xml"
  />
</Relationships>`;
}

function createRootRelsXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship
    Id="rId1"
    Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument"
    Target="xl/workbook.xml"
  />
</Relationships>`;
}

function createContentTypesXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml" />
  <Default Extension="xml" ContentType="application/xml" />
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml" />
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml" />
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml" />
</Types>`;
}

function createStylesXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2">
    <font>
      <sz val="11" />
      <name val="Aptos" />
    </font>
    <font>
      <b />
      <sz val="11" />
      <name val="Aptos" />
    </font>
  </fonts>
  <fills count="2">
    <fill><patternFill patternType="none" /></fill>
    <fill>
      <patternFill patternType="solid">
        <fgColor rgb="FFEAF1FF" />
        <bgColor indexed="64" />
      </patternFill>
    </fill>
  </fills>
  <borders count="1">
    <border>
      <left />
      <right />
      <top />
      <bottom />
      <diagonal />
    </border>
  </borders>
  <cellStyleXfs count="1">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" />
  </cellStyleXfs>
  <cellXfs count="2">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" />
    <xf numFmtId="0" fontId="1" fillId="1" borderId="0" xfId="0" applyFont="1" applyFill="1" />
  </cellXfs>
</styleSheet>`;
}

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);

  for (let index = 0; index < 256; index += 1) {
    let crc = index;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc & 1) === 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
    }
    table[index] = crc >>> 0;
  }

  return table;
})();

function computeCrc32(bytes: Uint8Array) {
  let crc = 0xffffffff;

  for (const value of bytes) {
    crc = CRC32_TABLE[(crc ^ value) & 0xff]! ^ (crc >>> 8);
  }

  return (crc ^ 0xffffffff) >>> 0;
}

function encodeUtf8(value: string) {
  return new TextEncoder().encode(value);
}

function uint16(value: number) {
  const output = new Uint8Array(2);
  const view = new DataView(output.buffer);
  view.setUint16(0, value, true);
  return output;
}

function uint32(value: number) {
  const output = new Uint8Array(4);
  const view = new DataView(output.buffer);
  view.setUint32(0, value, true);
  return output;
}

function concatBytes(parts: Uint8Array[]) {
  const totalLength = parts.reduce((sum, part) => sum + part.length, 0);
  const output = new Uint8Array(totalLength);
  let offset = 0;

  for (const part of parts) {
    output.set(part, offset);
    offset += part.length;
  }

  return output;
}

function createZip(entries: Array<{ data: string; name: string }>) {
  const localChunks: Uint8Array[] = [];
  const centralChunks: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = encodeUtf8(entry.name);
    const dataBytes = encodeUtf8(entry.data);
    const crc32 = computeCrc32(dataBytes);

    const localHeader = concatBytes([
      uint32(0x04034b50),
      uint16(20),
      uint16(0),
      uint16(0),
      uint16(0),
      uint16(0),
      uint32(crc32),
      uint32(dataBytes.length),
      uint32(dataBytes.length),
      uint16(nameBytes.length),
      uint16(0),
      nameBytes,
      dataBytes,
    ]);

    localChunks.push(localHeader);

    const centralHeader = concatBytes([
      uint32(0x02014b50),
      uint16(20),
      uint16(20),
      uint16(0),
      uint16(0),
      uint16(0),
      uint16(0),
      uint32(crc32),
      uint32(dataBytes.length),
      uint32(dataBytes.length),
      uint16(nameBytes.length),
      uint16(0),
      uint16(0),
      uint16(0),
      uint16(0),
      uint32(0),
      uint32(offset),
      nameBytes,
    ]);

    centralChunks.push(centralHeader);
    offset += localHeader.length;
  }

  const centralDirectory = concatBytes(centralChunks);
  const localDirectory = concatBytes(localChunks);
  const endOfCentralDirectory = concatBytes([
    uint32(0x06054b50),
    uint16(0),
    uint16(0),
    uint16(entries.length),
    uint16(entries.length),
    uint32(centralDirectory.length),
    uint32(localDirectory.length),
    uint16(0),
  ]);

  return concatBytes([localDirectory, centralDirectory, endOfCentralDirectory]);
}

function buildExcelWorkbook(headers: string[], rows: string[][]) {
  return createZip([
    { name: '[Content_Types].xml', data: createContentTypesXml() },
    { name: '_rels/.rels', data: createRootRelsXml() },
    { name: 'xl/workbook.xml', data: createWorkbookXml() },
    { name: 'xl/_rels/workbook.xml.rels', data: createWorkbookRelsXml() },
    { name: 'xl/styles.xml', data: createStylesXml() },
    { name: 'xl/worksheets/sheet1.xml', data: createWorksheetXml(headers, rows) },
  ]);
}

export function LeadsManagementPage() {
  const sessionState = authClient.useSession();
  const [leads, setLeads] = useState<LeadRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoadingSkeleton, setShowLoadingSkeleton] = useState(false);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [tableColumns, setTableColumns] = useState<typeof defaultTableColumns>(() => readStoredTableColumns());
  const [searchValue, setSearchValue] = useState('');
  const [provenanceFilter, setProvenanceFilter] = useState('all');
  const [resultFilter, setResultFilter] = useState<ResultFilter>('all');
  const [lotFilter, setLotFilter] = useState('all');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [sortPreset, setSortPreset] = useState<LeadSortPreset>('createdAt:desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof pageSizeOptions)[number]>(20);
  const debouncedSearchValue = useDebouncedValue(searchValue, 280);

  const fetchLeads = useCallback(async () => {
    if (sessionState.isPending || !sessionState.data?.user) {
      return;
    }

    setIsLoading(true);

    try {
      const sessionUser = sessionState.data.user as { role?: string | null } | undefined;
      const isAdmin = sessionUser?.role === 'admin';
      const { data, error } = await (isAdmin ? listLeads() : listUserLeads());

      if (error) {
        toast.error('Erreur lors du chargement des leads');
      } else if (data) {
        setLeads(data.data);
      }
    } finally {
      setIsLoading(false);
    }
  }, [sessionState.data, sessionState.isPending]);

  useEffect(() => {
    if (sessionState.isPending || !sessionState.data?.user) {
      return;
    }

    void fetchLeads();
  }, [fetchLeads, sessionState.data, sessionState.isPending]);

  useEffect(() => {
    if (sessionState.isPending || !isLoading) return;

    const timeoutId = window.setTimeout(() => {
      setShowLoadingSkeleton(true);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isLoading, sessionState.isPending]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(LEADS_TABLE_COLUMNS_STORAGE_KEY, JSON.stringify(tableColumns));
  }, [tableColumns]);

  const provenanceOptions = useMemo(() => {
    return Array.from(
      new Set(leads.map((lead) => `${lead.regionName || 'Région inconnue'} • ${lead.salonName || 'N/A'}`)),
    ).sort((left, right) => left.localeCompare(right, 'fr'));
  }, [leads]);

  const lotOptions = useMemo(() => {
    return Array.from(
      new Set(leads.map((lead) => lead.prizeName).filter((value): value is string => Boolean(value))),
    ).sort((left, right) => left.localeCompare(right, 'fr'));
  }, [leads]);

  const filteredLeads = useMemo(() => {
    const normalizedSearch = debouncedSearchValue.toLowerCase();

    return leads.filter((lead) => {
      const matchesSearch =
        lead.firstName.toLowerCase().includes(normalizedSearch)
        || lead.lastName.toLowerCase().includes(normalizedSearch)
        || lead.email.toLowerCase().includes(normalizedSearch)
        || lead.salonName?.toLowerCase().includes(normalizedSearch);

      if (!matchesSearch) {
        return false;
      }

      const provenanceLabel = `${lead.regionName || 'Région inconnue'} • ${lead.salonName || 'N/A'}`;
      if (provenanceFilter !== 'all' && provenanceLabel !== provenanceFilter) {
        return false;
      }

      if (resultFilter === 'won' && !lead.win) {
        return false;
      }

      if (resultFilter === 'lost' && lead.win) {
        return false;
      }

      if (lotFilter !== 'all' && (lead.prizeName || '-') !== lotFilter) {
        return false;
      }

      if (startDateFilter) {
        const leadDate = new Date(lead.createdAt);
        const startDate = new Date(`${startDateFilter}T00:00:00`);
        if (leadDate < startDate) {
          return false;
        }
      }

      if (endDateFilter) {
        const leadDate = new Date(lead.createdAt);
        const endDate = new Date(`${endDateFilter}T23:59:59`);
        if (leadDate > endDate) {
          return false;
        }
      }

      return true;
    });
  }, [debouncedSearchValue, endDateFilter, leads, lotFilter, provenanceFilter, resultFilter, startDateFilter]);

  const sortedLeads = useMemo(() => {
    const { sortKey, sortDirection } = parseSortPreset(sortPreset);
    const direction = sortDirection === 'asc' ? 1 : -1;

    return [...filteredLeads].sort((left, right) => {
      if (sortKey === 'name') {
        const leftValue = `${left.firstName} ${left.lastName}`.trim();
        const rightValue = `${right.firstName} ${right.lastName}`.trim();
        return leftValue.localeCompare(rightValue, 'fr', { sensitivity: 'base' }) * direction;
      }

      if (sortKey === 'result') {
        return ((Number(left.win) - Number(right.win)) * direction);
      }

      if (sortKey === 'provenance') {
        const leftValue = `${left.regionName || ''} ${left.salonName || ''}`.trim();
        const rightValue = `${right.regionName || ''} ${right.salonName || ''}`.trim();
        return leftValue.localeCompare(rightValue, 'fr', { sensitivity: 'base' }) * direction;
      }

      if (sortKey === 'lot') {
        const leftValue = left.prizeName || '';
        const rightValue = right.prizeName || '';
        return leftValue.localeCompare(rightValue, 'fr', { sensitivity: 'base' }) * direction;
      }

      const leftValue = new Date(left.createdAt).getTime();
      const rightValue = new Date(right.createdAt).getTime();
      return (leftValue - rightValue) * direction;
    });
  }, [filteredLeads, sortPreset]);

  const pageCount = Math.max(1, Math.ceil(sortedLeads.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const paginatedLeads = sortedLeads.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const currentRangeStart = sortedLeads.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const currentRangeEnd = Math.min(currentPage * pageSize, sortedLeads.length);
  const paginationItems = getPaginationItems(currentPage, pageCount);
  const visibleTableColumns = tableColumns.filter((column) => column.visible);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchValue, provenanceFilter, resultFilter, lotFilter, startDateFilter, endDateFilter, sortPreset, pageSize]);

  function clearProvenanceFilter() {
    setProvenanceFilter('all');
  }

  function clearResultFilter() {
    setResultFilter('all');
  }

  function clearLotFilter() {
    setLotFilter('all');
  }

  function clearDateFilters() {
    setStartDateFilter('');
    setEndDateFilter('');
  }

  function resetControls() {
    setSearchValue('');
    setProvenanceFilter('all');
    setResultFilter('all');
    setLotFilter('all');
    setStartDateFilter('');
    setEndDateFilter('');
    setSortPreset('createdAt:desc');
    setPage(1);
  }

  const activeFilters = [
    provenanceFilter !== 'all'
      ? {
          key: 'provenance',
          label: provenanceFilter,
          onClear: clearProvenanceFilter,
        }
      : null,
    resultFilter !== 'all'
      ? {
          key: 'result',
          label: resultFilterOptions.find((option) => option.value === resultFilter)?.label ?? resultFilter,
          onClear: clearResultFilter,
        }
      : null,
    lotFilter !== 'all'
      ? {
          key: 'lot',
          label: lotFilter === '-' ? 'Sans lot' : lotFilter,
          onClear: clearLotFilter,
        }
      : null,
    startDateFilter || endDateFilter
      ? {
          key: 'dates',
          label: `Période : ${startDateFilter || '...'} -> ${endDateFilter || '...'}`,
          onClear: clearDateFilters,
        }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; onClear: () => void }>;

  function handleExportExcel() {
    if (sortedLeads.length === 0) {
      toast.error('Aucune donnée à exporter');
      return;
    }

    const headers = ['Prénom', 'Nom', 'Email', 'Téléphone', 'Région', 'Salon', 'Résultat', 'Lot', 'Date'];
    const rows = sortedLeads.map((lead) => [
      lead.firstName,
      lead.lastName,
      lead.email,
      lead.phone || '',
      lead.regionName || '',
      lead.salonName || 'N/A',
      lead.win ? 'Gagné' : 'Perdu',
      lead.prizeName || '-',
      formatDate(lead.createdAt),
    ]);

    const excelContent = buildExcelWorkbook(headers, rows);
    const blob = new Blob([excelContent], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `leads-export-${new Date().toISOString().split('T')[0]}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Export XLS réussi');
  }

  const skeletonRowCount = Math.min(pageSize, 4);

  function resetTableColumns() {
    setTableColumns(defaultTableColumns);
  }

  function renderHeaderCell(column: (typeof defaultTableColumns)[number]) {
    if (column.key === 'contact') {
      const sortState = getSortState(sortPreset, column.key);
      return (
        <SortHeaderButton
          active={sortState.active}
          direction={sortState.direction}
          onClick={() => setSortPreset(sortPreset === 'name:asc' ? 'name:desc' : 'name:asc')}
        >
          {column.label}
        </SortHeaderButton>
      );
    }

    if (column.key === 'result') {
      const sortState = getSortState(sortPreset, column.key);
      return (
        <SortHeaderButton
          active={sortState.active}
          direction={sortState.direction}
          onClick={() => setSortPreset(sortPreset === 'result:desc' ? 'result:asc' : 'result:desc')}
        >
          {column.label}
        </SortHeaderButton>
      );
    }

    if (column.key === 'provenance') {
      const sortState = getSortState(sortPreset, column.key);
      return (
        <SortHeaderButton
          active={sortState.active}
          direction={sortState.direction}
          onClick={() => setSortPreset(sortPreset === 'provenance:asc' ? 'provenance:desc' : 'provenance:asc')}
        >
          {column.label}
        </SortHeaderButton>
      );
    }

    if (column.key === 'lot') {
      const sortState = getSortState(sortPreset, column.key);
      return (
        <SortHeaderButton
          active={sortState.active}
          direction={sortState.direction}
          onClick={() => setSortPreset(sortPreset === 'lot:asc' ? 'lot:desc' : 'lot:asc')}
        >
          {column.label}
        </SortHeaderButton>
      );
    }

    if (column.key === 'date') {
      const sortState = getSortState(sortPreset, column.key);
      return (
        <div className="text-right">
          <SortHeaderButton
            active={sortState.active}
            direction={sortState.direction}
            onClick={() => setSortPreset(sortPreset === 'createdAt:asc' ? 'createdAt:desc' : 'createdAt:asc')}
          >
            {column.label}
          </SortHeaderButton>
        </div>
      );
    }

    return <span className="font-semibold text-foreground">{column.label}</span>;
  }

  function renderLeadCell(columnKey: LeadTableColumnKey, lead: LeadRecord) {
    if (columnKey === 'contact') {
      return (
        <div className="flex flex-col">
          <span className="font-semibold text-[15px] text-foreground">
            {lead.firstName} {lead.lastName}
          </span>
          <TruncatedValue className="mt-0.5 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <MailIcon className="size-3" />
              {lead.email}
              {lead.phone ? (
                <>
                  <span className="mx-1">•</span>
                  <span>{lead.phone}</span>
                </>
              ) : null}
            </div>
          </TruncatedValue>
        </div>
      );
    }

    if (columnKey === 'provenance') {
      return (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-[14px] font-medium text-foreground">
            <MapPinIcon className="size-3.5 text-primary/70" />
            {lead.regionName}
          </div>
          <div className="text-xs text-muted-foreground">{lead.salonName || 'N/A'}</div>
        </div>
      );
    }

    if (columnKey === 'result') {
      return lead.win ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
          <TrophyIcon className="size-3.5" />
          Gagné
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-300">
          <XCircleIcon className="size-3.5" />
          Perdu
        </span>
      );
    }

    if (columnKey === 'lot') {
      return lead.prizeName ? (
        <div className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground">
          <TicketIcon className="size-3.5 text-primary/70" />
          {lead.prizeName}
        </div>
      ) : (
        <span className="text-sm text-muted-foreground">-</span>
      );
    }

    return (
      <div className="inline-flex items-center gap-1.5">
        <CalendarIcon className="size-3.5" />
        {formatDate(lead.createdAt)}
      </div>
    );
  }

  return (
    <section className="flex min-h-[calc(100svh-10rem)] flex-col gap-6 text-foreground">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">Collecte de Leads</h1>
            <span className="inline-flex min-w-8 items-center justify-center rounded-full border border-border bg-card px-2.5 py-1 text-sm font-medium text-muted-foreground">
              {sortedLeads.length}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Consultez et exportez les contacts recueillis via les QR codes en salon.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={handleExportExcel}
            className="h-12 border-black bg-black px-6 text-[15px] font-medium text-white shadow-none transition-colors hover:border-black/85 hover:bg-black/85 hover:text-white dark:border-border dark:bg-muted dark:text-foreground dark:hover:border-foreground/20 dark:hover:bg-muted/80 dark:hover:text-foreground"
          >
            <DownloadIcon className="mr-2 size-4" />
            Exporter XLS
          </Button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[var(--radius-surface-4xl)] border border-border/70 bg-card">
        <div className="border-b border-border/70 bg-[#f4f4f538] p-4 sm:p-5 dark:bg-muted/40">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex w-full max-w-[42rem] items-center gap-2">
              <div className="relative min-w-0 flex-1 max-w-[34rem]">
                <SearchIcon className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder="Rechercher les leads"
                  className="h-12 border-border bg-background pl-11 pr-11 text-[15px] shadow-none placeholder:text-muted-foreground dark:bg-muted"
                />
                {searchValue ? (
                  <button
                    type="button"
                    onClick={() => setSearchValue('')}
                    className="absolute top-1/2 right-3 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <XIcon className="size-4" />
                  </button>
                ) : null}
              </div>

              <ToolbarButton onClick={() => setIsFilterSheetOpen(true)} className="shrink-0 gap-2 px-4">
                Filtres
                <Settings2Icon className="size-4" />
              </ToolbarButton>
            </div>

            <div className="flex items-center justify-end gap-2 xl:ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <ToolbarButton className="justify-between pr-6 xl:min-w-56">
                    {getSortPresetLabel(sortPreset)}
                    <ChevronDownIcon className="size-4" />
                  </ToolbarButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Ordre d’affichage</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuRadioGroup value={sortPreset} onValueChange={(value) => setSortPreset(value as LeadSortPreset)}>
                    {sortPresetOptions.map((option) => (
                      <DropdownMenuRadioItem key={option.value} value={option.value}>
                        {option.label}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>

              <UsersTableColumnsPopover
                columns={tableColumns}
                onChange={(columns) => setTableColumns(columns as typeof defaultTableColumns)}
                onReset={resetTableColumns}
              />
            </div>
          </div>

          {activeFilters.length > 0 ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {activeFilters.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={filter.onClear}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:border-foreground/20 hover:bg-muted dark:bg-muted dark:hover:bg-muted/80"
                >
                  <span>{filter.label}</span>
                  <XIcon className="size-3.5 text-muted-foreground" />
                </button>
              ))}

              {activeFilters.length >= 3 ? (
                <button
                  type="button"
                  onClick={resetControls}
                  className="text-sm font-medium text-black underline underline-offset-4 transition-colors hover:text-black/80 dark:text-foreground dark:hover:text-foreground/80"
                >
                  Effacer tout
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          <table className="min-w-full border-separate border-spacing-0">
            <thead>
              <tr className="text-left text-sm text-muted-foreground">
                {visibleTableColumns.map((column) => (
                  <th key={column.key} className="px-6 py-4">
                    {renderHeaderCell(column)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && showLoadingSkeleton
                ? Array.from({ length: skeletonRowCount }).map((_, index) => (
                    <tr key={`skeleton-${index}`}>
                      {visibleTableColumns.map((column) => (
                        <td key={column.key} className="border-t border-border px-6 py-5 align-middle">
                          <Skeleton className={cn('h-5 rounded-full', column.key === 'contact' ? 'w-40' : column.key === 'date' ? 'ml-auto w-24' : 'w-28')} />
                        </td>
                      ))}
                    </tr>
                  ))
                : paginatedLeads.map((lead) => (
                    <tr key={lead.id} className="align-middle">
                      {visibleTableColumns.map((column) => (
                        <td key={column.key} className={cn('border-t border-border px-6 py-5 align-middle', column.key === 'date' ? 'text-right text-sm text-muted-foreground' : undefined)}>
                          {renderLeadCell(column.key, lead)}
                        </td>
                      ))}
                    </tr>
                  ))}
            </tbody>
          </table>

          {!isLoading && paginatedLeads.length === 0 ? (
            <div className="flex min-h-[24rem] flex-1 flex-col items-center justify-center gap-3 border-t border-border px-6 py-16 text-center">
              <div className="flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <TicketIcon className="size-6" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-foreground">Aucun lead ne correspond à cette vue</h3>
                <p className="max-w-md text-sm leading-6 text-muted-foreground">
                  Ajustez la recherche, les filtres ou l’ordre d’affichage pour retrouver rapidement la bonne participation.
                </p>
              </div>
            </div>
          ) : null}
        </div>

        <div id="leads-pagination" className="grid gap-3 border-t border-border px-6 py-4 md:grid-cols-[1fr_auto_1fr] md:items-center">
          <p className="text-sm text-muted-foreground">
            {currentRangeStart}-{currentRangeEnd} sur {sortedLeads.length} leads affichés
          </p>

          <Pagination className="justify-center">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#leads-pagination"
                  aria-disabled={currentPage <= 1}
                  tabIndex={currentPage <= 1 ? -1 : 0}
                  className={cn(currentPage <= 1 ? 'pointer-events-none opacity-50' : undefined)}
                  onClick={(event) => {
                    event.preventDefault();
                    if (currentPage <= 1) return;
                    setPage((value) => value - 1);
                  }}
                />
              </PaginationItem>

              {paginationItems.map((pageNumber, index) => {
                const previousPage = paginationItems[index - 1];
                const shouldRenderEllipsis = previousPage && pageNumber - previousPage > 1;

                return (
                  <Fragment key={pageNumber}>
                    {shouldRenderEllipsis ? (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : null}
                    <PaginationItem>
                      <PaginationLink
                        href="#leads-pagination"
                        isActive={pageNumber === currentPage}
                        onClick={(event) => {
                          event.preventDefault();
                          setPage(pageNumber);
                        }}
                      >
                        {pageNumber}
                      </PaginationLink>
                    </PaginationItem>
                  </Fragment>
                );
              })}

              <PaginationItem>
                <PaginationNext
                  href="#leads-pagination"
                  aria-disabled={currentPage >= pageCount}
                  tabIndex={currentPage >= pageCount ? -1 : 0}
                  className={cn(currentPage >= pageCount ? 'pointer-events-none opacity-50' : undefined)}
                  onClick={(event) => {
                    event.preventDefault();
                    if (currentPage >= pageCount) return;
                    setPage((value) => value + 1);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>

          <div className="flex justify-start md:justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ToolbarButton className="h-10 justify-between pl-4 pr-5 text-sm">
                  {pageSize} lignes
                  <ChevronDownIcon className="size-4" />
                </ToolbarButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Lignes par page</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value) as (typeof pageSizeOptions)[number])}>
                  {pageSizeOptions.map((option) => (
                    <DropdownMenuRadioItem key={option} value={String(option)}>
                      {option} lignes
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
        <SheetContent side="right" className="w-full border-border sm:max-w-md">
          <SheetHeader className="border-b border-border">
            <SheetTitle>Filtres</SheetTitle>
            <SheetDescription>
              Affinez la table par provenance, résultat, lot et période.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-8 overflow-y-auto px-6 py-6">
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Provenance</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setProvenanceFilter('all')}
                  className={cn(
                    'rounded-full border px-4 py-2 text-sm transition-colors',
                    provenanceFilter === 'all'
                      ? 'border-black bg-black text-white'
                      : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                  )}
                >
                  Toutes les provenances
                </button>
                {provenanceOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setProvenanceFilter(option)}
                    className={cn(
                      'rounded-full border px-4 py-2 text-sm transition-colors',
                      provenanceFilter === option
                        ? 'border-black bg-black text-white'
                        : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                    )}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Résultat</h3>
              <div className="flex flex-wrap gap-2">
                {resultFilterOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setResultFilter(option.value)}
                    className={cn(
                      'rounded-full border px-4 py-2 text-sm transition-colors',
                      resultFilter === option.value
                        ? 'border-black bg-black text-white'
                        : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Lot</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setLotFilter('all')}
                  className={cn(
                    'rounded-full border px-4 py-2 text-sm transition-colors',
                    lotFilter === 'all'
                      ? 'border-black bg-black text-white'
                      : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                  )}
                >
                  Tous les lots
                </button>
                <button
                  type="button"
                  onClick={() => setLotFilter('-')}
                  className={cn(
                    'rounded-full border px-4 py-2 text-sm transition-colors',
                    lotFilter === '-'
                      ? 'border-black bg-black text-white'
                      : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                  )}
                >
                  Sans lot
                </button>
                {lotOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setLotFilter(option)}
                    className={cn(
                      'rounded-full border px-4 py-2 text-sm transition-colors',
                      lotFilter === option
                        ? 'border-black bg-black text-white'
                        : 'border-border bg-background text-foreground hover:border-foreground/20 hover:bg-muted',
                    )}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Période</h3>
              <div className="grid gap-3">
                <Input type="date" value={startDateFilter} onChange={(event) => setStartDateFilter(event.target.value)} />
                <Input type="date" value={endDateFilter} onChange={(event) => setEndDateFilter(event.target.value)} />
              </div>
            </div>
          </div>

          <SheetFooter className="border-t border-border">
            <Button variant="outline" onClick={resetControls}>
              Réinitialiser
            </Button>
            <Button onClick={() => setIsFilterSheetOpen(false)}>Appliquer</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  );
}
